import { PayloadAction } from '@reduxjs/toolkit';
import { ZLBoltMode } from '@zerolight-core/libzl';
import { produce, WritableDraft } from 'immer';

import { StreamConnectionState } from '@zerolight-core/libzl-react';
import { ConflictDetails, IBuildSession, StartBuildSession } from '@zerolight-core/generic-buildsession';
import { AppThunk, AppDispatch, RootState, ThunkAPIType } from '../store';
import { buildCreateSlice, asyncThunkCreator } from '@reduxjs/toolkit'
import { CustomWrap, DefaultAppConfigType, ImageMedia, ModelProxy, ItemCompatibility as OptionCompatibility, OptionData, OptionsGroup, VisualizerConfiguration, VisualizerOptionsCategory, VisualizerStateActions } from '../../types/VisualizerTypes';
import { composeState } from '../../utils/VisualizerUtils';
import { CustomUnitData, IgnoredOptions, ItemWithCustomData } from '../../BuildSession';
import { getInitialUrlArgs } from '../../UrlArgsProvider';
import { MessageDescriptor } from 'react-intl';
import { BuildData } from '@zerolight-core/iframe-communications';
import { OpaqueBuildData } from '../../helpers';

export const createAppSlice = buildCreateSlice({
  creators: { asyncThunk: asyncThunkCreator },
})

export const StarterPackWrap = "starter_pack_wrap"

export type FocusedOption = {
    optionGroup: string,
    optionId: string,
    option: OptionData
}

export interface UndoEntry {
    buildSession: IBuildSession;
    state: Partial<State>;
}

export type State = {
    config: DefaultAppConfigType
    //currentLocale: string,
    optionGroups: VisualizerConfiguration["configuration"]["optionsGroups"],
    currentVisualizerConfig: VisualizerConfiguration,
    selectedOptions: VisualizerConfiguration["configuration"]["selectedOptions"],
    modesReady: { "2D": boolean, "3D": boolean },
    modesBusy: { "2D": boolean, "3D": boolean },
    showTooltip: boolean,
    streamLoading: boolean,
    reconnectAllowed: boolean,
    currentMode: ZLBoltMode,
    requestedMode: ZLBoltMode,
    idleTimeout?: number | undefined,
    idleTimeoutEpoch?: number | undefined,
    activeCategory: string,
    sceneOptionGroup: string | undefined,
    focusedOption: FocusedOption | undefined,
    previewSelection?: VisualizerConfiguration["configuration"]["selectedOptions"],
    previewWrap?: CustomWrap
    ready: boolean,
    undoMessage?: { message: MessageDescriptor, values: Record<string, unknown> };
    hasUserOptions?: boolean;
    currentCustomWrap?: string;
    saved?: boolean
    changes: number
    pendingColourPicker: boolean
}

const initialState = (cfg?: DefaultAppConfigType, previousState?: State) =>
{
    console.log("Initial state...");
    const config = cfg!;
    
    const visualizerConfiguration = config.visualizer;

    const state: State = {
        config,
        currentVisualizerConfig: visualizerConfiguration,

        optionGroups: visualizerConfiguration.configuration.optionsGroups,
        selectedOptions: { ...visualizerConfiguration.configuration.selectedOptions },
        modesReady: { "2D": false, "3D": false },
        modesBusy: { "2D": false, "3D": false },
        showTooltip: true,
        streamLoading: true,
        reconnectAllowed: false,
        currentMode: previousState?.currentMode ?? "2D" as ZLBoltMode,
        requestedMode: previousState?.requestedMode ?? "3D" as ZLBoltMode,

        activeCategory: previousState?.activeCategory && visualizerConfiguration.categories.map(c => c.id).includes(previousState?.activeCategory) ? previousState?.activeCategory : visualizerConfiguration.categories[0]?.id ?? "starter_packs",
        sceneOptionGroup: previousState?.sceneOptionGroup as string | undefined,
        focusedOption: previousState?.focusedOption,
        previewSelection: undefined,
        ready: false,
        saved: false,
        changes: 0,
        pendingColourPicker: false
    }
    return state;
};

type SliceState = ReturnType<typeof VisualizerSlice["getInitialState"]>

function filterObject<TKey extends string, TValue>(obj: Record<TKey, TValue>, predicate: (v:[TKey, TValue]) => boolean )
{
    return Object.fromEntries(Object.entries(obj).filter(predicate as (v: [string, unknown]) => boolean)) as Record<TKey, TValue>;
}

function firstKey<T extends string>(obj: Record<T, unknown>)
{
    return Object.entries(obj)[0]?.[0] as T | undefined;
}

function ValidateState(state: WritableDraft<SliceState>)
{
    state.optionGroups = { ...state.currentVisualizerConfig.configuration.optionsGroups };
    for(const groupId in state.optionGroups) {
        state.optionGroups[groupId] = {
            ...state.optionGroups[groupId],
            options: {
                ...state.optionGroups[groupId].options,
            }
        }
    }
    
    for (const groupId in state.selectedOptions)
    {
        let optionsIds = state.selectedOptions[groupId].filter(o => o in state.optionGroups[groupId].options);

        if (optionsIds.length == 0 && !state.optionGroups[groupId].canBeEmpty)
        {
            const fallbackOption = firstKey(state.optionGroups[groupId].options);
            optionsIds = state.selectedOptions[groupId] = fallbackOption ? [fallbackOption] : [];
        }

        const options = optionsIds.map(o => state.currentVisualizerConfig.configuration.optionsGroups[groupId].options[o])
        
        for (const option of options)
        {
            if (option.compatibility?.includes)
            {
                for (const g in option.compatibility.includes)
                {
                    if(g === "cameras") {
                        if (g in state.optionGroups && option.compatibility!.includes![g].length) {
                            console.log(`Processing ${g} : ${option.displayName.defaultMessage}`,Object.keys(state.optionGroups[g].options) )
                            state.optionGroups[g].options = Object.entries(state.optionGroups[g].options).reduce((p, [i,o]) => {
                                const includes = Array.from(option.compatibility!.includes![g])
                                const n = includes.includes(i) ? {...o, disabled: false, hidden: false} : o
                                console.log("Option", i, n, n.disabled, n.hidden)
                                p =Object.assign(p, {[i]: n})
                                console.log(p)
                                return p
                            }, {})
                        }
                    }
                    else {
                        if (g in state.optionGroups && option.compatibility!.includes![g].length) {
                            state.optionGroups[g].options = filterObject(state.optionGroups[g].options, (v) => option.compatibility!.includes![g].includes(v[0]))
                        }
                    }
                }
            }
            if (option.compatibility?.excludes)
            {
                for (const g in option.compatibility.excludes)
                {
                    if (g in state.optionGroups)
                        state.optionGroups[g].options = filterObject(state.optionGroups[g].options, (v) => {if(option.compatibility!.excludes![g].includes(v[0])) {console.log(`Filtering ${g} : ${v[0]} because of ${option.displayName.defaultMessage}`)}; return !option.compatibility!.excludes![g].includes(v[0])})
                }
            }
        }
        const category = state.currentVisualizerConfig.categories.find(c => c.id == state.activeCategory) as VisualizerOptionsCategory;
       
        if (category?.compatibility)
        {
            if (category.compatibility?.includes)
            {
                for (const g in category.compatibility.includes)
                {
                    if (g in state.optionGroups && category.compatibility!.includes![g].length)
                        state.optionGroups[g].options = filterObject(state.optionGroups[g].options, (v) => category.compatibility!.includes![g].includes(v[0]))
                }

            }
            if (category.compatibility?.excludes)
            {
                for (const g in category.compatibility.excludes)
                {
                    if (g in state.optionGroups)
                        state.optionGroups[g].options = filterObject(state.optionGroups[g].options, (v) => !category.compatibility!.excludes![g].includes(v[0]))
                }
            }
        }
    }

    state.optionGroups["cameras"].options = Object.entries(state.optionGroups["cameras"].options).reduce((p, [i, o]) => {
        if(!o.hidden) {
            p[i] = o
        }
        return p
    }, {} as Record<string, OptionData>)
}

export const VisualizerSlice = createAppSlice({
    name: 'Visualizer',
    initialState: null as never as State,
    reducers: {
        updateConfig: (state, action: PayloadAction<DefaultAppConfigType>):State =>
        {
            const result = {
                ...state,
                ...initialState(action.payload, state as State),
            };
            
            return produce(result, ValidateState);
        },
        // setCurrentVisualizer: (state, action: PayloadAction<string>) =>
        // {
        //     const visualizerConfig = state.config.catalogue.visualizers[action.payload];
        //     state.currentVisualizerConfig = state.config.catalogue.visualizers[action.payload];
        //     state.selectedOptions = { ...visualizerConfig.configuration.selectedOptions };
        //     state.activeCategory = visualizerConfig.categories[0].id;
        //     ValidateState(state)
        //     return state;
        // },
        optionToggle: (state, action: PayloadAction<{group: string, option: string}>) => {
            const group = state.currentVisualizerConfig.configuration.optionsGroups[action.payload.group];
            let groupOptions = state.selectedOptions[action.payload.group];

            if(group.options[action.payload.option]?.onSelect) {
                VisualizerSlice.caseReducers.handleActions(state, { type: "", payload: { actions: group.options[action.payload.option].onSelect } });
            }

            if(groupOptions.includes(action.payload.option)){
                if (group.canBeEmpty || group.selectionMode == "multiple" && groupOptions.length > 1)
                    groupOptions = groupOptions.filter(o => o != action.payload.option);
            } else if(group.selectionMode == "single"){
                groupOptions = [action.payload.option]
            } else {
                groupOptions = [...groupOptions, action.payload.option]
            }

            state.selectedOptions[action.payload.group] = groupOptions;
            ValidateState(state);
        },
        setSaved: (state, action: PayloadAction<boolean>) => {
            state.saved = action.payload
        },
        incrementChanges: (state) => {
            state.changes = state.changes + 1   
        },
        setChanges: (state, action: PayloadAction<number>) => {
            state.changes = action.payload
        },
        setPendingColourPicker: (state, action: PayloadAction<boolean>) => {
            state.pendingColourPicker = action.payload
        },
        setIdleTimer: (state, action: PayloadAction<number | undefined>) =>
        {
            state.idleTimeout = action.payload;
            state.idleTimeoutEpoch = Date.now();
        },
        handleActions: (state, action: PayloadAction<{ actions?: VisualizerStateActions }>) =>
        {
            const actions = action.payload.actions;
            if (actions?.updateSelection)
            {
                state.selectedOptions = {
                    ...state.selectedOptions,
                    ...actions.updateSelection,
                }
                ValidateState(state);

            }
            if (actions?.toggleGroups)
            {
                for (const key of Object.keys(actions.toggleGroups))
                    for (const option of actions.toggleGroups[key])
                        VisualizerSlice.caseReducers.optionToggle(state, { type: "", payload: { group: key, option } })
            }
        },

        setActiveCategory: (state, { payload: categoryId}: PayloadAction<string>) =>
        {
            const category = state.currentVisualizerConfig.categories.find(c => c.id == categoryId)! as VisualizerOptionsCategory;
            state.activeCategory = categoryId;

            VisualizerSlice.caseReducers.handleActions(state, { type: "", payload: { actions: category.onActivate } });
            state.focusedOption = undefined;
            state.previewSelection = undefined;
            state.previewWrap = undefined;
        },

        nextCategory: (state) =>
        {
            const nextCategoryIndex = state.currentVisualizerConfig.categories.findIndex(c => c.id == state.activeCategory) + 1;
            const category = state.currentVisualizerConfig.categories[nextCategoryIndex];
            if (category)
                VisualizerSlice.caseReducers.setActiveCategory(state, { type: "", payload: category.id });
        },
        focusOption: (state, action: PayloadAction<Omit<FocusedOption, "option"> | undefined>) =>
        {
            if (!action.payload)
                return;

            if (state.focusedOption?.optionGroup == action.payload.optionGroup && state.focusedOption?.optionId == action.payload?.optionId) {
                state.focusedOption = undefined;
                state.previewSelection = undefined;
            }
            else
            {
                state.focusedOption = { ...action.payload, option: state.optionGroups[action.payload.optionGroup].options[action.payload.optionId] };
                if (action.payload?.optionGroup && action.payload?.optionId)
                {
                    const option = state.optionGroups[action.payload.optionGroup].options[action.payload?.optionId];
                    if (option?.onSelect)
                    {
                        VisualizerSlice.caseReducers.handleActions(state, { type: "", payload: { actions: option?.onSelect } });
                    }
                }
                
            }

        },
        visualizerClosed: (state) =>
        {
            state.currentMode = "2D";
            state.requestedMode = "3D";
            state.streamLoading = true;
        },
        setModeState: (state, { payload }: PayloadAction<{ mode: ZLBoltMode, ready: boolean, streamState: StreamConnectionState }>) =>
        {
            if (state.modesReady[payload.mode] != payload.ready)
                state.modesReady[payload.mode] = payload.ready;

            if (payload.mode == state.requestedMode && payload.ready)
                state.currentMode = payload.mode;

            if (payload.mode == "3D")
                state.streamLoading = payload.streamState == StreamConnectionState.Connecting;

            if (payload.mode == "3D" && payload.ready)
            {
                state.currentMode = payload.mode;
                state.modesReady["2D"] = false;
                state.showTooltip = true;
                state.reconnectAllowed = false;
            }
            if (payload.mode == "3D" && !payload.ready && (payload.streamState == StreamConnectionState.Disconnected || payload.streamState == StreamConnectionState.ConnectionError) )
            {
                if ((state.currentVisualizerConfig.allowedModes ?? ["2D", "3D"]).includes("2D"))
                {
                    state.requestedMode = "2D";
                    if (state.modesReady["2D"])
                        state.currentMode = "2D";
                    state.streamLoading = false;
                    state.reconnectAllowed = false;
                }
                else
                    state.reconnectAllowed = false;
            }
        },
        nextCamera(state, action: PayloadAction<number>)
        {
            const current = state.selectedOptions["cameras"][0];
            const cameras = Object.keys(state.optionGroups["cameras"].options);
            const index = cameras.indexOf(current);
            state.selectedOptions["cameras"][0] = cameras[(index + Math.max(action.payload, -cameras.length) + cameras.length) % cameras.length];
        },
        reconnect: (state) =>
        {
            state.reconnectAllowed = true;
        },
        setMode: (state, action: PayloadAction<ZLBoltMode>) =>
        {
            if (state.modesReady[action.payload])
                state.currentMode = action.payload;
            state.requestedMode = action.payload;
            state.reconnectAllowed = true;
        },
        interactedWithStream: (state) => {
            state.showTooltip = false;
            if (state.requestedMode == "2D")
            {
                state.requestedMode = "3D";
                state.reconnectAllowed = true;
            }
        },
        setSceneOptionGroup: (state, action: PayloadAction<string | undefined>) => {
            state.sceneOptionGroup = action.payload;
        },
        setStreamBusy: (state, action: PayloadAction<{ mode: "2D" | "3D", busy: boolean }>) => {
            state.modesBusy[action.payload.mode] = action.payload.busy;
        },
        setSelection: (state, action: PayloadAction<Record<string, string[]>>) => {
            state.selectedOptions = action.payload;
        },
        setState(_, action: PayloadAction<State>):State
        {
            return produce(action.payload, ValidateState);
        },
        setPreviewWrap: (state, action: PayloadAction<CustomWrap|undefined>) => {
            state.previewWrap = action.payload
        },
        addCustomWrap: (state, action: PayloadAction<{id: string, wrap: CustomWrap}>) => {
            state.currentVisualizerConfig.configuration.customWraps[action.payload.id] = action.payload.wrap
            state.saved = false
        },
        toggleCustomWrap: (state, action: PayloadAction<string>) => {
            const enable = !state.currentVisualizerConfig.configuration.customWraps[action.payload].selected
            state.currentVisualizerConfig.configuration.customWraps[action.payload].selected = enable
            if(enable) {
                Object.entries(state.currentVisualizerConfig.configuration.customWraps).forEach(([id, wrap]) => {
                    if(id !== action.payload) {
                        wrap.selected = false
                    }
                })
            }
            state.saved = false
        },
        deselectAllCustomWraps: (state) => {
            Object.values(state.currentVisualizerConfig.configuration.customWraps).forEach(wrap => wrap.selected = false)
            state.saved = false
        },
        removeCustomWrap: (state, action: PayloadAction<string | undefined>) => {
            if(!action.payload) {
                state.currentVisualizerConfig.configuration.customWraps = {}
            }
            else if(state.currentVisualizerConfig.configuration.customWraps[action.payload] !== undefined) {
                delete state.currentVisualizerConfig.configuration.customWraps[action.payload]
            }
            state.saved = false
        },
        setUndoMessage(state, action: PayloadAction<State["undoMessage"]>)
        {
            state.undoMessage = action.payload;
            state.hasUserOptions = false;
        },
        userUpdatesOptions(state)
        {
            state.hasUserOptions = true;
            state.undoMessage = undefined;
        },
        dismissUndo(state)
        {
            state.undoMessage = undefined;  
        },
        editWrapRequest(state)
        {
            state.currentCustomWrap = state.focusedOption?.optionId;
        },
        editWrapDone(state)
        {
            state.currentCustomWrap = undefined;
        }
    },
    selectors: {
        visualiserState: (state) =>
        {
            return composeState(state.currentVisualizerConfig, state.previewSelection ? {...state.selectedOptions, ...state.previewSelection } : state.selectedOptions, state.previewWrap, state.focusedOption);
        },

        initialVisualiserState: (state) =>
        {
            return composeState(state.currentVisualizerConfig, state.currentVisualizerConfig.configuration.selectedOptions, state.previewWrap, state.focusedOption);
        },
    }
});

export function deselectOptions(payload: { group: string, options: string[] }):AppThunk
{
    return async (dispatch, getState) =>
    {
        IgnoredOptions.push(...payload.options);

        const activeOption = getState().configuration.selectedOptions[payload.group].find(id => payload.options.includes(id));
        if (activeOption)
            dispatch(optionToggle({ group: payload.group, option: activeOption }));
    }
    
}

export function optionClick(payload: { group: string, option: string }): AppThunk
{
    return getInitialUrlArgs().itemsAutoApply ? optionToggle(payload) : previewOption(payload);
}

export function findOption(optionId: string): AppThunk<OptionData | undefined>
{
    return (_, getState) =>
    {
        const state = getState();
        for (const groupId in state.configuration.optionGroups)
        {
            const group = state.configuration.optionGroups[groupId];
            const option = group.options[optionId];
            if (option)
                return option;
        }
        return undefined;
    }
}

export function previewOption(payload: { group: string, option: string }):AppThunk
{
    return async (dispatch, getState, thunkAPI) =>
    {
        dispatch(VisualizerSlice.actions.focusOption(payload && {optionGroup: payload.group, optionId: payload.option}))

        const state = getState()

        if (!state.configuration.focusedOption)
            return;

        let selectedOptions = state.configuration.selectedOptions;

        if (!payload || selectedOptions[payload.group].includes(payload.option))
        {
            const updatedState = produce(getState().configuration, (state) =>
            {    
                state.previewSelection = undefined;
            });
            dispatch(VisualizerSlice.actions.setState(updatedState));
            return;
        }

        const onAutoFixConflict = (details: ConflictDetails) => {
            details.resolutions[0].apply()
        }

        const buildSession = thunkAPI.currentBuildSession;
        let previewSession = buildSession?.clone((build) => { previewSession = build }, onAutoFixConflict, buildSession.onAttachChoice, buildSession.onLoadInvalid);

        if (!previewSession)
            return;

        const optionData = getState().configuration.optionGroups[payload.group].options[payload.option];
        const previewWrap: CustomWrap | undefined = optionData.customWrap;
        const selectedItems: Record<string, string[]> = {};
        
        if (previewSession && optionData.buildSessionUpdate)
        {
            const { overrideDate } = getInitialUrlArgs()

            await StartBuildSession(previewSession.session.userId!, crypto.randomUUID(), previewSession.onBuildSessionUpdate, previewSession.onConflict, previewSession.onAttachChoice, previewSession.onLoadInvalid, optionData.buildSessionUpdate, overrideDate);
        }
        else
        {
            const item = previewSession?.getItem(payload.option);
            if (item)
            {
                const attachPoints = item.validAttachPoints;
                const attachPoint = attachPoints?.[0];
                const selectedItems = previewSession?.getSelectedItems(true);
                const matchingItems = selectedItems?.filter(i => i.attachedPoints?.[0] === attachPoint)
                if (matchingItems?.length && item.id !== matchingItems[0].id)
                {
                    item.onReplace?.(matchingItems[0]);
                }
                else {
                    item.onAdd?.()
                }
            } else
            {
                selectedItems[payload.group] = [payload.option];
            }
        }

        //const optionGroups = readItems(previewSession);
        const selected = getSelection(previewSession, {});
        
        selectedOptions = Object.fromEntries(
            Object.entries(selectedOptions)
                .map(([key, value]) => [key, value.filter(o => !getState().configuration.currentVisualizerConfig.configuration.optionsGroups[key].options[o].buildSessionOption)])
                .filter(([, value]) => value.length)
        );

        const newSelection = {
            ...selectedOptions,
            ...selectedItems,
            //...Object.fromEntries(Object.keys(optionGroups).map(groupId => [groupId, []])),
            ...{ body_style: ["TB009"], wheels: ["STOCK_WHEEL"] },
            ...(previewWrap ? { "wrap": ["preview"] } : optionData.buildSessionUpdate ? { wrap: [] } : {}),
            ...selected,
        };

        const updatedState = produce(getState().configuration, (state) =>
        {
            if (previewWrap)
                state.currentVisualizerConfig.configuration.optionsGroups["wrap"].options = { ...state.currentVisualizerConfig.configuration.optionsGroups["wrap"].options, preview: { ...getOptionFromWrap(previewWrap), hidden: true },}

            state.previewSelection = newSelection;
        });
        dispatch(VisualizerSlice.actions.setState(updatedState));
    }
}


export function optionToggle(payload: {group: string, option: string, buildSession?: IBuildSession}):AppThunk
{
    return async (dispatch, getState, thunkAPI) =>
    {
        const build = payload.buildSession ?? thunkAPI.currentBuildSession;
        const configuration = getState().configuration
        const optionData = configuration.optionGroups[payload.group].options[payload.option];
        const focused = getState().configuration.focusedOption;

        const {itemsAutoApply, overrideDate} = getInitialUrlArgs()
        if ((focused?.optionGroup != payload.group || focused.optionId != payload.option))
        {
            dispatch(VisualizerSlice.actions.focusOption(payload && {optionGroup: payload.group, optionId: payload.option}))
        }

        if (optionData.buildSessionUpdate && build)
        {
            if (getState().configuration.hasUserOptions)
                dispatch(pushHistory());

            if (getState().configuration.hasUserOptions || getState().configuration.undoMessage)
                dispatch(VisualizerSlice.actions.setUndoMessage({ message: { id: 'starter_pack.undo_message', defaultMessage: "Your build has been overwritten by <b>{name}</b>" }, values: { name: optionData.displayName } }))

            dispatch(VisualizerSlice.actions.deselectAllCustomWraps())

            //Remove any custom wraps added by a previous starter pack
            dispatch(VisualizerSlice.actions.removeCustomWrap(StarterPackWrap))

            //Create & apply any custom wraps associated with this build
            if(optionData.customWrap) {
                dispatch(VisualizerSlice.actions.addCustomWrap({id: StarterPackWrap, wrap: {...optionData.customWrap, created: Date.now(), selected: true}}))
            }
            
            const newBuild = await StartBuildSession(build.session.userId!, crypto.randomUUID(), build.onBuildSessionUpdate, build.onConflict, build.onAttachChoice, build.onLoadInvalid, optionData.buildSessionUpdate, overrideDate);
            dispatch(updateBuildSession(newBuild));

            if (!itemsAutoApply)
                dispatch(VisualizerSlice.actions.nextCategory());

            dispatch(VisualizerSlice.actions.setSaved(false))
            return;
        }

        dispatch(VisualizerSlice.actions.userUpdatesOptions());
        const variant = build?.getItem(payload.option);
        if (!variant)
        {
            if(payload.group === "wrap") {
                dispatch(VisualizerSlice.actions.toggleCustomWrap(payload.option))
                const selectedItems = build?.getSelectedItems()
                const appliedWrap = selectedItems?.find(i => (i as ItemWithCustomData).customData.tags.find(tag => tag.id ==="wrap"))
                if(appliedWrap) {
                    appliedWrap.onRemove?.()
                }
            }

            dispatch(VisualizerSlice.actions.optionToggle(payload));

            return;
        }

        dispatch(VisualizerSlice.actions.setSaved(false))

        const attachPoints = variant.validAttachPoints;
        const attachPoint = attachPoints?.[0];
        if (variant.selected) {
            variant.onRemove?.();
        }
        else
        {
            const selectedItems = build?.getSelectedItems(true);
            const matchingItems = selectedItems?.filter(i => i.attachedPoints?.[0] === attachPoint)
            if (matchingItems?.length && variant.id !== matchingItems[0].id)
            {
                variant.onReplace?.(matchingItems[0]);
            }
            else {
                variant.onAdd?.()

                if(payload.group === "wrap") {
                    //Remove any custom wrap
                    const selectedCustomWrap = Object.entries(configuration.currentVisualizerConfig.configuration.customWraps).find(([,w]) => w.selected)
                    if(selectedCustomWrap) {
                        dispatch(VisualizerSlice.actions.toggleCustomWrap(selectedCustomWrap[0]))
                    }
                }
            }
        }
        console.log("Item toggled");
    }
}

export function cleanSlate() : AppThunk {
    return async (dispatch, _, thunkAPI) =>
        {
            const {overrideDate} = getInitialUrlArgs()
            const build = thunkAPI.currentBuildSession;
            if(build) {
                const blankSlate = {
                    manufacturer: "slate",
                    model: "truck",
                    year: "2025",
                    pkg: "truck",
                }
                const newBuild = await StartBuildSession(build.session.userId!, crypto.randomUUID(), build.onBuildSessionUpdate, build.onConflict, build.onAttachChoice, build.onLoadInvalid, blankSlate, overrideDate);
                dispatch(VisualizerSlice.actions.deselectAllCustomWraps())
                dispatch(VisualizerSlice.actions.removeCustomWrap(StarterPackWrap))
                dispatch(updateBuildSession(newBuild));
                dispatch(VisualizerSlice.actions.setSaved(false))
                return;
            }
        }
}

export function loadSavedBuild(savedBuild: BuildData) : AppThunk {
    return async (dispatch, _, thunkAPI) =>
        {
            const {overrideDate} = getInitialUrlArgs()
            const build = thunkAPI.currentBuildSession;
            if(build) {
                const savedBuildData = savedBuild.data as unknown as OpaqueBuildData
                const data = new Blob([savedBuildData.session], { type: "application/zerolight-build" })
                const newBuild = await StartBuildSession(build.session.userId!, crypto.randomUUID(), build.onBuildSessionUpdate, build.onConflict, build.onAttachChoice, build.onLoadInvalid, data, overrideDate);
                dispatch(VisualizerSlice.actions.deselectAllCustomWraps())
                dispatch(VisualizerSlice.actions.removeCustomWrap())
                for(const [id, wrap] of Object.entries(savedBuildData.customWraps)) {
                    dispatch(VisualizerSlice.actions.addCustomWrap({id, wrap}))
                }
                dispatch(updateBuildSession(newBuild));
                dispatch(VisualizerSlice.actions.setSaved(true))
                return;
            }
        }
}

function getItemGroup(item: ItemWithCustomData)
{
    return item.customData.tags[0]?.id ?? "unknown";
}

function getThumbnail(id: string, dependencies: string[], prcodes: string[], customUnitData:CustomUnitData): Partial<ImageMedia>
{
    const template = customUnitData.thumbnails.find(thumbnail => thumbnail.id == id)
    if (!template)
    {
        return {
            type: "image",
            url: id
        }
    }
    const frameConfig = template.frameConfig;

    if (frameConfig)
    {
        const thumbnail : ImageMedia = {
            type: "image",
            frameConfig: {
                ...frameConfig,
                version: frameConfig?.version,
                camera: {...frameConfig.camera},
                prcodes: dependencies.join(","),
                environment: "parking_lot",
                render_quality: "Fast"
            }
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const camera = thumbnail.frameConfig!.camera as unknown as {data: any}
        if(camera.data?.Advanced?.ZLCertifiedEffectsValues?.ZLCert_PartHighlighting?.PRCodes) {
            camera.data = {
                ...camera.data,
                Advanced: {
                    ...camera.data.Advanced,
                    ZLCertifiedEffectsValues: {
                        ...camera.data.Advanced.ZLCertifiedEffectsValues,
                        ZLCert_PartHighlighting: undefined /*{
                            ...camera.data.Advanced.ZLCertifiedEffectsValues.ZLCert_PartHighlighting,
                            PRCodes: prcodes.join(",")
                        }*/
                    }
                }
            }
        }

        return thumbnail
    }
    
    return {
        type: "image",
        url: template.url
    }
}

function readItems(buildSession: IBuildSession)
{
    const optionGroups: Record<string, OptionsGroup> = {};
    const customUnitData = buildSession.getCustomData() as CustomUnitData;

    function getThumbnails(item: ItemWithCustomData) : {thumbnail: ImageMedia, thumbnailSummary: ImageMedia, thumbnailPreview: ImageMedia, proxyModel?: ModelProxy}
    {
        const prcodes = item.customData.prcodes;
        const dependencies = item.customData.thumbnailPRCodes
        if(item.customData.wrap) {
            function rgbToStr(rgb: {r: number, g: number, b: number}) {
                return `rgb(${rgb.r},${rgb.g},${rgb.b})`
            }
            return {
                thumbnail: { type: "swatch", colour: rgbToStr(item.customData.wrap.diffuse), finish: item.customData.wrap.finish, custom: false } as ImageMedia,
                thumbnailSummary: { type: "swatch", colour: rgbToStr(item.customData.wrap.diffuse), finish: item.customData.wrap.finish, custom: false } as ImageMedia,
                thumbnailPreview: { type: "swatch", colour: rgbToStr(item.customData.wrap.diffuse), finish: item.customData.wrap.finish, custom: false } as ImageMedia
            } as const
        }
        return {
            thumbnail: { url: null, type: "image", ...getThumbnail(item.customData.thumbnail, dependencies, prcodes, customUnitData) } as ImageMedia,
            thumbnailSummary: { url: null, type: "image", ...getThumbnail(item.customData.thumbnailSummary, dependencies, prcodes, customUnitData) } as ImageMedia,
            thumbnailPreview: { url: null, type: "image", ...getThumbnail(item.customData.thumbnailPreview, dependencies, prcodes, customUnitData) } as ImageMedia,
            proxyModel: item.customData.proxyModel,
        } as const
    }

    const subcategories : Record<string, {variants: ItemWithCustomData[], groupId: string}> = {}
    const items = buildSession.getAllItems() as ItemWithCustomData[]

    const compatibility: Record<string, OptionCompatibility> = {
        "TB009": {
            excludes: {
                cameras: ["RearSeats"],
                exterior: [
                    //"EU009",
                    "EU017",
                    "EU0FB",
                ],
                interior: [
                    "IA001-SUV-ARM-BLCK",
                    "IA019-SUV-ARM-BRWN",
                ]
            },
        },
        "TB009-SUV": {
            excludes: {
                exterior: [
                    "TB013",
                    "TB013_ONLY_REAR",

                    "EU009",
                    //"EU017",
                    "EU0FB",
                ],
            },
        },
        "SUV_COVER_OFF": {
            excludes: {
                exterior: [
                    "TB013",
                    "TB013_ONLY_REAR",

                    //"EU009",
                    "EU017",
                    "EU0FB",
                ],
            },
        },
        "TB009-Fastback": {
            excludes: {
                exterior: [
                    "TB013",
                    "TB013_ONLY_REAR",

                    "EU009",
                    "EU017",
                    //"EU0FB",
                ]
            }
        },
        "IA-Slatelets-SLogo": {
            includes: {
                cameras: ["Slatelets"]
            }
        },
        "IA-Slatelets-SUV-Holographic": {
            includes: {
                cameras: ["Slatelets"]
            }
        },
        "IA-Slatelets-TruckGraffiti": {
            includes: {
                cameras: ["Slatelets"]
            }
        },
        "IA-Slatelets-FastbackGID": {
            includes: {
                cameras: ["Slatelets"]
            }
        },
        "IA-Slatelets-BeHold": {
            includes: {
                cameras: ["Slatelets"]
            }
        },
        "IA-Slatelets-MI-Bird": {
            includes: {
                cameras: ["Slatelets"]
            }
        },
        "IA-Slatelets-Paw": {
            includes: {
                cameras: ["Slatelets"]
            }
        },
        "IA-Slatelets-26.2": {
            includes: {
                cameras: ["Slatelets"]
            }
        }
    };

    for (const item of items)
    {
        const option:OptionData = {
            displayName: { id: `item.${item.id}.name`, defaultMessage: item.name},
            hoverName: {id: `item.${item.id}.short_name`, defaultMessage: item.customData.shortName },
            description: { id: `item.${item.id}.description`, defaultMessage: item.description },
            ...getThumbnails(item),
            shape: item.customData.thumbnailShape as "square" | "rectangular" | undefined,
            visualizerState: {
                prcodes: item.customData!["prcodes"],
            },
            buildSessionOption: true,
            subcategories: item.customData.subcategory,
            compatibility: compatibility[item.id],
            hideInSummary: item.customData.hideInSummary,
            characteristic: item.validAttachPoints?.join(),
        };

        const groupId = getItemGroup(item);
        const group = optionGroups[groupId] = optionGroups[groupId] ?? {
            canBeEmpty: true,
            options: {},
            selectionMode: ["body_style", "wrap"].includes(groupId) ? "single" : "multiple",
        };

        //console.log(categoryId);
        if (item.customData?.featureCamera)
        {
            option.onSelect = {
                updateSelection: {
                    cameras: [item.customData?.featureCamera]
                }
            }
        }

        if (item.id === "A004" || item.id === "A004_C")
        {
            option.onSelect = {
                updateSelection: {
                    cameras: [
                        "AudioTech",
                    ]
                }
            };

            option.focusedVisualizerState = {
                animationStates: {
                    BIN: 1,
                },
                forceAnimationStates: {
                    BIN: 1
                }
            }
        }

        group.options[item.id] = option;


        for(const subcategory of option.subcategories ?? []) {
            if(!subcategories[subcategory]) {
                subcategories[subcategory] = {groupId, variants: []}
            }
            subcategories[subcategory].variants.push(item)
        }
    }

    return optionGroups;
}

function getOptionFromWrap(wrap: CustomWrap): OptionData {
    const option: OptionData = {
        displayName: { id: "item.custom_wrap.name", defaultMessage: "Custom Wrap"},
        description: { id: `item.custom_wrap.description`, defaultMessage: ""},
        thumbnail: {type: "swatch", colour: `rgb(${wrap.diffuse.r}, ${wrap.diffuse.g}, ${wrap.diffuse.b})`, finish: wrap.finish, custom: true},
        thumbnailPreview: {type: "swatch", colour: `rgb(${wrap.diffuse.r}, ${wrap.diffuse.g}, ${wrap.diffuse.b})`, finish: wrap.finish, custom: true},
        thumbnailSummary: {type: "swatch", colour: `rgb(${wrap.diffuse.r}, ${wrap.diffuse.g}, ${wrap.diffuse.b})`, finish: wrap.finish, custom: true},
        visualizerState: {
            prcodes: [`custom_${wrap.finish}`,`custom_${wrap.finish}__D(${wrap.diffuse.r}:${wrap.diffuse.g}:${wrap.diffuse.b})__S(${wrap.specular.r}:${wrap.specular.g}:${wrap.specular.b})`],
        },
        buildSessionOption: false,
        customWrap: wrap,
        subcategories: ["wrap"]
    };
    return option;
}

function injectWrapItems(optionGroups: Record<string, OptionsGroup>, customWraps: Record<string, CustomWrap>) {
    const wraps = Object.entries(customWraps).sort((l,r) => l[1].created - r[1].created)
    for(const [id, wrap] of wraps) {
        const option: OptionData = getOptionFromWrap(wrap);

        const groupId = "wrap";
        const group = optionGroups[groupId] = optionGroups[groupId] ?? {
            canBeEmpty: true,
            options: {},
            selectionMode: "single", //"multiple"
        };

        group.options = {[id]: option, ...group.options};
    }
}

function getSelection(buildSession: IBuildSession, customWraps: Record<string, CustomWrap>)
{
    const newSelection:Record<string, string[]> = {};
    for (const selectedItem of buildSession?.getSelectedItems(true) as ItemWithCustomData[])
    {
        if(selectedItem.attachedPoints?.length === 0) {
            continue //Ignore items that aren't attached
        }
        const groupId = getItemGroup(selectedItem);
        newSelection[groupId] = [...newSelection[groupId] ?? [], selectedItem.id];
    }
    console.log("Custom Wraps:", JSON.stringify(customWraps))
    for(const [id] of Object.entries(customWraps).filter(([,wrap]) => wrap.selected)) {
        const groupId = "wrap"
        newSelection[groupId] = [...newSelection[groupId] ?? [], id]
    }
    console.log("New Selection:", JSON.stringify(newSelection))
    return newSelection;
}

export function undo() : AppThunk
{
    return (dispatch: AppDispatch, getState: () => RootState, thunkAPI: ThunkAPIType) =>
    {
        if (!thunkAPI.undoStack.length)
            return;

        const entry = thunkAPI.undoStack.pop();
        entry?.buildSession.onBuildSessionUpdate(entry.buildSession, false);
        dispatch(VisualizerSlice.actions.setUndoMessage(undefined));
        dispatch(VisualizerSlice.actions.userUpdatesOptions());
        let state = getState().configuration;
        state = {
            ...state,
            ...entry?.state,
        }
        dispatch(VisualizerSlice.actions.setState(state));
    }
}

export function pushHistory() : AppThunk
{
    return (dispatch: AppDispatch, getState: () => RootState, thunkAPI: ThunkAPIType) =>
    {
        if (thunkAPI.currentBuildSession)
            thunkAPI.undoStack.push({
                buildSession: thunkAPI.currentBuildSession,
                state: getState().configuration,
            });
    }
}

export function updateBuildSession(buildSession: IBuildSession) : AppThunk
{
    return (dispatch: AppDispatch, getState: () => RootState, thunkAPI: ThunkAPIType) =>
    {
        thunkAPI.currentBuildSession = buildSession;
        
        console.debug("BuildSession Thunk Update", buildSession.state);
        
        const state = getUpdatedState(buildSession, getState)

        dispatch(VisualizerSlice.actions.setState(state));
    }
}

export function addCustomWrap(id: string, wrap: CustomWrap) : AppThunk
{
    return (dispatch: AppDispatch, getState: () => RootState, thunkAPI: ThunkAPIType) => {
        dispatch(VisualizerSlice.actions.addCustomWrap({id, wrap}))
        if(thunkAPI.currentBuildSession) {
            const state = getUpdatedState(thunkAPI.currentBuildSession, getState)

            dispatch(VisualizerSlice.actions.setState(state));
        }
    }
}

export function removeCustomWrap(id: string) : AppThunk
{
    return (dispatch: AppDispatch, getState: () => RootState, thunkAPI: ThunkAPIType) => {
        
        dispatch(pushHistory());
        dispatch(VisualizerSlice.actions.setUndoMessage({ message: { id: 'custom_wrap.undo_message', defaultMessage: "Custom wrap removed." }, values: {} }))

        dispatch(VisualizerSlice.actions.removeCustomWrap(id))
        if(thunkAPI.currentBuildSession) {
            const state = getUpdatedState(thunkAPI.currentBuildSession, getState)

            dispatch(VisualizerSlice.actions.setState(state));
        }
    }
}

function getUpdatedState(buildSession: IBuildSession, getState: () => RootState)
{
    const customUnitData = buildSession.getCustomData() as CustomUnitData;
    const optionGroups = readItems(buildSession);

    return produce(getState().configuration, (state) => {

        const categories: VisualizerOptionsCategory[] = []
            
            for (const item of customUnitData.tags)
            {
                const category: VisualizerOptionsCategory = {
                    id: item.id,
                    group: item.id,
                    displayName: { id: `tag.${item.id}.name` },
                    nothingSelectedMessage: { id: `tag.${item.id}.description` },
                    shape: item.thumbnailShape as "square" | "rectangular" | undefined,
                    onActivate: {
                        updateSelection: {
                            cameras: [item.camera],
                        }
                    }
                }
                categories.push(category);
            }

            const starterPacks: OptionsGroup = state.currentVisualizerConfig.configuration.optionsGroups["starter_packs"] = {
                canBeEmpty: true,
                selectionMode: "single",
                options: {}
            }

            for (const template of customUnitData.templates)
            {
                starterPacks.options[template.id] = {
                    displayName: { id: `starter_pack.${template.id}.name` },
                    hoverName: {id: `starter_pack.${template.id}.short_name`},
                    description: { id: `starter_pack.${template.id}.description` },
                    thumbnail: <ImageMedia>{
                        url: null,
                        type: "image",
                        ...getThumbnail(template.thumbnail, [], template.prcodes, customUnitData),
                    },
                    thumbnailPreview: <ImageMedia>{
                        url: null,
                        type: "image",
                        ...getThumbnail(template.thumbnailPreview, [], template.prcodes, customUnitData),
                    },
                    buildSessionUpdate: {
                        manufacturer: "slate",
                        model: "truck",
                        pkg: "truck",
                        year: "2025",
                        desiredOptions: template.includes,
                    },
                    visualizerState: {},
                    customWrap: template.customWrap
                }
            }
        
        injectWrapItems(optionGroups, state.currentVisualizerConfig.configuration.customWraps)
        
        const selected = getSelection(buildSession, state.currentVisualizerConfig.configuration.customWraps);
        console.log("Selected Buildsession options", selected);
        state.selectedOptions = {
            ...state.selectedOptions,
            ...Object.fromEntries(Object.keys(optionGroups).map(groupId => [groupId, []])),
            ...{body_style: ["TB009"], wheels: ["STOCK_WHEEL"]},
            ...selected
        };
        state.currentVisualizerConfig.configuration.optionsGroups = {
            ...state.currentVisualizerConfig.configuration.optionsGroups,
            ...optionGroups,
        }
        state.optionGroups = state.currentVisualizerConfig.configuration.optionsGroups;
        state.currentVisualizerConfig.categories = categories;

        state.ready = true;
        state.previewSelection = undefined;
        state.previewWrap = undefined;
        return state;
    });
}